import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';

export type Tema = 'dark' | 'light';

/** Misma clave que usaba el CRM anterior: quien ya eligió tema no lo pierde. */
const CLAVE_ALMACEN = 'lexi_theme';

interface ContextoTema {
  tema: Tema;
  alternar: () => void;
  fijar: (tema: Tema) => void;
  /** `true` mientras el tema venga de la preferencia del sistema y no de una elección. */
  siguiendoAlSistema: boolean;
}

const Contexto = createContext<ContextoTema | null>(null);

function leerGuardado(): Tema | null {
  try {
    const valor = localStorage.getItem(CLAVE_ALMACEN);
    return valor === 'dark' || valor === 'light' ? valor : null;
  } catch {
    // Modo privado o almacenamiento bloqueado: se sigue funcionando sin recordar.
    return null;
  }
}

function preferenciaDelSistema(): Tema {
  return window.matchMedia('(prefers-color-scheme: light)').matches ? 'light' : 'dark';
}

/**
 * Tema claro/oscuro.
 *
 * Tres detalles que hacen la diferencia entre un interruptor que funciona y uno
 * que molesta:
 *
 * 1. **Sin destello al cargar.** El tema ya viene aplicado por el script en
 *    `index.html`, que corre antes que React. Este proveedor lo lee del DOM en
 *    vez de volver a decidirlo, así que no hay un parpadeo claro al arrancar
 *    en oscuro.
 * 2. **Respeta el sistema hasta que el usuario opine.** Mientras no haya
 *    elección guardada se sigue `prefers-color-scheme` en vivo — si el sistema
 *    cambia a oscuro al anochecer, la app acompaña. En cuanto toca el
 *    interruptor, esa elección manda y deja de seguir al sistema.
 * 3. **Se sincroniza entre pestañas.** Cambiar el tema en una pestaña lo cambia
 *    en las demás; tener el CRM abierto dos veces con temas distintos es
 *    desconcertante.
 */
export function ProveedorTema({ children }: { children: ReactNode }) {
  const [tema, setTema] = useState<Tema>(() => {
    const enDom = document.documentElement.getAttribute('data-theme');
    if (enDom === 'dark' || enDom === 'light') return enDom;
    return leerGuardado() ?? preferenciaDelSistema();
  });

  const [siguiendoAlSistema, setSiguiendoAlSistema] = useState(() => leerGuardado() === null);

  // Aplicar al DOM. Es lo único que realmente cambia los colores: todo el CSS
  // cuelga de `[data-theme]`.
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', tema);
  }, [tema]);

  // Seguir al sistema mientras no haya elección explícita.
  useEffect(() => {
    if (!siguiendoAlSistema) return;

    const consulta = window.matchMedia('(prefers-color-scheme: light)');
    const alCambiar = (evento: MediaQueryListEvent) => setTema(evento.matches ? 'light' : 'dark');

    consulta.addEventListener('change', alCambiar);
    return () => consulta.removeEventListener('change', alCambiar);
  }, [siguiendoAlSistema]);

  // Sincronizar entre pestañas.
  useEffect(() => {
    const alCambiarAlmacen = (evento: StorageEvent) => {
      if (evento.key !== CLAVE_ALMACEN) return;

      if (evento.newValue === 'dark' || evento.newValue === 'light') {
        setTema(evento.newValue);
        setSiguiendoAlSistema(false);
      } else if (evento.newValue === null) {
        setSiguiendoAlSistema(true);
        setTema(preferenciaDelSistema());
      }
    };

    window.addEventListener('storage', alCambiarAlmacen);
    return () => window.removeEventListener('storage', alCambiarAlmacen);
  }, []);

  const fijar = useCallback((nuevo: Tema) => {
    setTema(nuevo);
    setSiguiendoAlSistema(false);
    try {
      localStorage.setItem(CLAVE_ALMACEN, nuevo);
    } catch {
      // Sin almacenamiento el tema dura la sesión. Preferible a romper el clic.
    }
  }, []);

  const alternar = useCallback(() => {
    fijar(tema === 'dark' ? 'light' : 'dark');
  }, [tema, fijar]);

  const valor = useMemo(
    () => ({ tema, alternar, fijar, siguiendoAlSistema }),
    [tema, alternar, fijar, siguiendoAlSistema],
  );

  return <Contexto.Provider value={valor}>{children}</Contexto.Provider>;
}

export function useTema(): ContextoTema {
  const contexto = useContext(Contexto);
  if (!contexto) throw new Error('useTema debe usarse dentro de <ProveedorTema>.');
  return contexto;
}
